import React from "react";
import {
  Box,
  Button,
  Stack,
  Text,
  useMantineTheme,
} from "@mantine/core";
import Navigation from "./navigation";
import AppFooter from "./app-footer";

// Default Done handler — forwards the terminal status to the
// publisher exactly like DataContext.postMessage does, including
// the React Native bridge if present.
const defaultOnDone = (status) => {
  if (
    typeof window !== "undefined" &&
    window.ReactNativeWebView?.postMessage
  ) {
    window.ReactNativeWebView.postMessage(
      JSON.stringify({ status, source: "result-screen" }),
    );
    return;
  }
  if (typeof window !== "undefined") {
    window.parent.postMessage(status, "*");
  }
};

// Compact tinted result chip, Cash-App style. The chip pops in with
// a back-overshoot bounce, then a mark strokes through it. PASS
// uses the active Mantine primary color (so it follows the
// publisher's brand color); FAIL is always red[6] on a red[1] tint
// since "failure = red" is a near-universal signal.
function AnimatedResultChip({ status = "PASS", size = 48 }) {
  const theme = useMantineTheme();
  const isPass = status === "PASS";
  const primaryShades =
    theme.colors[theme.primaryColor] || theme.colors.teal;

  const tint = isPass
    ? primaryShades[1] || "rgba(0, 201, 100, 0.18)"
    : theme.colors.red[1];
  const accent = isPass
    ? primaryShades[6] || "#00C964"
    : theme.colors.red[6];

  // Both marks fit inside a 100x100 viewBox at the same stroke
  // weight so the chip reads consistently across PASS / FAIL.
  // Check path total length ≈ 60.8, X paths total ≈ 64.
  const mark = isPass ? (
    <path
      d="M30 52 L45 67 L72 38"
      fill="none"
      stroke={accent}
      strokeWidth="8"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{
        strokeDasharray: 65,
        strokeDashoffset: 65,
        animation: "resultChipDrawMark 320ms ease-out 360ms forwards",
      }}
    />
  ) : (
    <g>
      <path
        d="M34 34 L66 66"
        fill="none"
        stroke={accent}
        strokeWidth="8"
        strokeLinecap="round"
        style={{
          strokeDasharray: 46,
          strokeDashoffset: 46,
          animation:
            "resultChipDrawMark 240ms ease-out 360ms forwards",
        }}
      />
      <path
        d="M66 34 L34 66"
        fill="none"
        stroke={accent}
        strokeWidth="8"
        strokeLinecap="round"
        style={{
          strokeDasharray: 46,
          strokeDashoffset: 46,
          animation:
            "resultChipDrawMark 240ms ease-out 540ms forwards",
        }}
      />
    </g>
  );

  return (
    <Box
      sx={{
        width: size,
        height: size,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "@keyframes resultChipPop": {
          "0%": { transform: "scale(0.4)", opacity: 0 },
          "60%": { transform: "scale(1.08)", opacity: 1 },
          "100%": { transform: "scale(1)", opacity: 1 },
        },
        "@keyframes resultChipDrawMark": {
          "0%": {},
          "100%": { strokeDashoffset: 0 },
        },
        animation:
          "resultChipPop 460ms cubic-bezier(0.34, 1.56, 0.64, 1) both",
      }}
    >
      <svg
        viewBox="0 0 100 100"
        width={size}
        height={size}
        style={{ display: "block" }}
      >
        <circle cx="50" cy="50" r="48" fill={tint} />
        {mark}
      </svg>
    </Box>
  );
}

// Visual port of the inline PASS/FAIL result rendering currently
// living in DataContext.js (lines ~1594-1624). Pulled out as its
// own component so we can preview/iterate in Storybook without
// touching the production render path. When we're happy with the
// new visual, DataContext can swap to use this component directly.
export default function ResultScreen({
  status = "PASS",
  successTitle = "You've been verified",
  successSubTitle = "You're all set — you can safely close this window.",
  failureTitle = "We couldn't verify you",
  failureSubTitle =
    "We weren't able to complete your verification. You can safely close this window.",
  logoUrl = "",
  showNavigation = true,
  // When false, the screen renders without a Done button — used in
  // legacy flows that still auto-fire the publisher message on
  // status flip. When true (default), the parent should DELAY the
  // automatic terminal postMessage and rely on this button as the
  // explicit user ack.
  showDoneButton = true,
  doneLabel = "Done",
  onDone,
}) {
  const isPass = status === "PASS";
  const handleDone = () => {
    if (typeof onDone === "function") {
      onDone(status);
      return;
    }
    defaultOnDone(status);
  };

  return (
    <Box
      sx={(t) => ({
        // Fills whatever container the parent provides. On mobile /
        // narrow the parent wraps us at 100vh; on wide desktop the
        // parent wraps us inside the same 400x700 card App.js uses
        // for its journey routes so the result screen lines up with
        // the rest of the flow.
        height: "100%",
        minHeight: "100%",
        backgroundColor:
          t.colorScheme === "dark" ? t.colors.dark[7] : t.white,
        display: "flex",
        flexDirection: "column",
      })}
    >
      {showNavigation && (
        <Navigation logo_url={logoUrl} forceHideClose />
      )}

      {/* Content area — top-aligned icon/title/subtitle, flex spacer,
          then Done button pinned at the bottom of the content area.
          `minHeight: 0` lets the flex child shrink so the footer
          below always stays in view. */}
      <Box
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          padding: "32px 24px 24px",
        }}
      >
        <Stack spacing="md" align="flex-start">
          <AnimatedResultChip status={status} size={48} />
          <Stack spacing={6}>
            <Text fz={24} fw={700}>
              {isPass ? successTitle : failureTitle}
            </Text>
            <Text c="dimmed" fz="md">
              {isPass ? successSubTitle : failureSubTitle}
            </Text>
          </Stack>
        </Stack>

        <Box style={{ flex: 1 }} />

        {showDoneButton && (
          <Button
            fullWidth
            size="lg"
            onClick={handleDone}
            styles={{ root: { color: "#fff" } }}
          >
            {doneLabel}
          </Button>
        )}
      </Box>

      {/* Same footer the journey shows on every other route so the
          screen doesn't lose the Powered By / Privacy Policy chrome
          after replacing App.js's wrapper. */}
      <AppFooter />
    </Box>
  );
}
